import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import {
  getCSAWeeklySummaries,
  getTeamWeeklySummaries,
} from '../../../api/Summaries/query';
import { ExtendedUser } from '../../../Types';
import useTmAlias from './useTmAlias';
import useSummaryTargetLanguage from './useSummaryTargetLanguage';

interface UseHasHadSummariesProps {
  alias: string;
  directReports: ExtendedUser[];
  isProfileTm: boolean;
  profilesLoaded: boolean;
}

/**
 * Custom hook to determine if a user (CSA or TM) has had any weekly summaries generated.
 *
 * This hook is used to conditionally render the CSA Summaries component - if a user has
 * never had any summaries, the component should not be displayed.
 *
 * For **CSA profiles**: Queries the user's own weekly summaries and returns true if they
 * have at least one summary.
 *
 * For **TM profiles**: Queries the team's weekly summaries for all direct reports and
 * returns true if at least one direct report has had a summary (indicated by the
 * `hasHadSummary` flag).
 *
 * Due to query deduplication - if this is called within a page that is already rendering CSA summaries, there will be no duplicate calls.
 */

const useHasHadSummaries = ({
  alias,
  directReports,
  isProfileTm,
  profilesLoaded,
}: UseHasHadSummariesProps) => {
  const tmAlias = useTmAlias();
  const targetLanguage = useSummaryTargetLanguage();

  const directReportsAliases = useMemo(
    () => directReports.map(reportee => reportee.alias),
    [directReports]
  );

  const { data: teamWeeklySummaries, loading: teamWeeklySummariesLoading } =
    useQuery(getTeamWeeklySummaries, {
      fetchPolicy: 'cache-first',
      skip: !profilesLoaded || !isProfileTm || tmAlias === '',
      variables: {
        aliases: directReportsAliases,
        managerAlias: tmAlias,
        targetLanguage,
      },
    });

  const { data: csaWeeklySummary, loading: csaWeeklySummaryLoading } = useQuery(
    getCSAWeeklySummaries,
    {
      fetchPolicy: 'cache-first',
      skip: !profilesLoaded || isProfileTm || tmAlias === '',
      variables: {
        alias,
        managerAlias: tmAlias,
        limit: 1,
        targetLanguage,
      },
    }
  );

  const reportsHasHadSummaries = useMemo(() => {
    return teamWeeklySummaries?.getTeamLatestSummaries.results.some(
      summary => summary.hasHadSummary
    );
  }, [teamWeeklySummaries?.getTeamLatestSummaries.results]);

  const hasHadSummary =
    (isProfileTm && reportsHasHadSummaries) ||
    (!isProfileTm &&
      csaWeeklySummary?.getCSAWeeklySummaries?.summaries?.length !== 0);

  return {
    loading: csaWeeklySummaryLoading || teamWeeklySummariesLoading,
    hasHadSummary,
  };
};

export default useHasHadSummaries;
